import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Loader2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordDialog({ trigger }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm(""); setCode(""); setCodeSent(false);
  };

  const sendCode = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/change-password/request-code");
      setCodeSent(true);
      toast.success(`Verification code sent to ${data.to}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not send verification code");
    } finally { setSending(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (isAdmin && !code.trim()) { toast.error("Enter the verification code from your email"); return; }
    setSaving(true);
    try {
      const payload = { current_password: current, new_password: next };
      if (isAdmin) payload.code = code.trim();
      await api.post("/auth/change-password", payload);
      toast.success("Password updated. Use the new one at your next login.");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="h-9 rounded-lg" data-testid="change-password-trigger">
            <KeyRound className="w-4 h-4 mr-1" /> Change password
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>
            {isAdmin ? (
              <>Enter a new password (min 8 chars) and confirm with the 6-digit code we email to <b>{user?.email}</b>.</>
            ) : (
              "Enter your current password and a new one (min 8 characters)."
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="cp-current" className="text-xs">Current password</Label>
            <Input id="cp-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required className="h-11" data-testid="cp-current" />
          </div>
          <div>
            <Label htmlFor="cp-next" className="text-xs">New password</Label>
            <Input id="cp-next" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} className="h-11" data-testid="cp-new" />
          </div>
          <div>
            <Label htmlFor="cp-confirm" className="text-xs">Confirm new password</Label>
            <Input id="cp-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="h-11" data-testid="cp-confirm" />
          </div>

          {isAdmin && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 space-y-2" data-testid="cp-verify-block">
              <div className="text-xs text-blue-900 font-bold uppercase tracking-wide flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Extra step for admin
              </div>
              {!codeSent ? (
                <Button type="button" onClick={sendCode} disabled={sending} variant="outline" className="h-10 rounded-lg font-semibold" data-testid="cp-send-code">
                  {sending ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-1.5" /> Email me a code</>}
                </Button>
              ) : (
                <>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="h-11 font-mono text-lg tracking-widest text-center"
                    data-testid="cp-code"
                  />
                  <button type="button" onClick={sendCode} className="text-xs text-blue-700 underline" data-testid="cp-resend-code">
                    Didn't get it? Resend
                  </button>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="submit" disabled={saving || (isAdmin && !codeSent)} className="bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="cp-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
